/**
 * Analytics System - Tracks game events for Vercel Analytics
 *
 * Events tracked per PRD section 9:
 * - level_start: When a level begins
 * - level_complete: When a level is successfully completed
 * - level_fail: When the player loses (game over)
 * - session_start: When the game session begins
 * - session_end: When the game session ends
 * - gate_choice: When the player passes through a multiplier gate
 *
 * Privacy:
 * - No personally identifiable information
 * - No tracking across sessions (no user ID)
 * - Data used only for game improvement
 */

import { track } from '@vercel/analytics';

// =============================================================================
// SESSION STATE
// =============================================================================

const analyticsEnabled = import.meta.env.PROD;
type AnalyticsValue = string | number | boolean | null | undefined;

function safeTrack(event: string, payload: Record<string, AnalyticsValue>): void {
  if (!analyticsEnabled) return;
  track(event, payload);
}

interface SessionState {
  /** Timestamp when session started */
  startTime: number;
  /** Number of levels played in this session */
  levelsPlayed: number;
  /** Whether session_start has been tracked */
  hasTrackedStart: boolean;
}

const sessionState: SessionState = {
  startTime: Date.now(),
  levelsPlayed: 0,
  hasTrackedStart: false,
};

// =============================================================================
// SESSION EVENTS
// =============================================================================

/**
 * Tracks session start event
 * Should be called once when the game first loads
 */
export function trackSessionStart(): void {
  if (sessionState.hasTrackedStart) return;

  sessionState.startTime = Date.now();
  sessionState.levelsPlayed = 0;
  sessionState.hasTrackedStart = true;

  safeTrack('session_start', {
    timestamp: new Date().toISOString(),
  });
}

/**
 * Tracks session end event
 * Called automatically when the page unloads
 */
export function trackSessionEnd(): void {
  if (!sessionState.hasTrackedStart) return;

  const durationSeconds = Math.floor((Date.now() - sessionState.startTime) / 1000);

  safeTrack('session_end', {
    duration_seconds: durationSeconds,
    levels_played: sessionState.levelsPlayed,
  });
}

// =============================================================================
// LEVEL EVENTS
// =============================================================================

/**
 * Tracks when a level starts
 */
export function trackLevelStart(levelId: number, tier: number): void {
  safeTrack('level_start', {
    level_id: levelId,
    tier: tier,
  });
}

/**
 * Tracks when a level is successfully completed
 */
export function trackLevelComplete(
  levelId: number,
  tier: number,
  score: number,
  soldiersRemaining: number
): void {
  sessionState.levelsPlayed++;

  safeTrack('level_complete', {
    level_id: levelId,
    tier: tier,
    score: score,
    soldiers_remaining: soldiersRemaining,
  });
}

/**
 * Tracks when the player fails a level (game over)
 */
export function trackLevelFail(
  levelId: number,
  tier: number,
  soldiersAtDeath: number,
  failPosition: number,
  context?: {
    reason?: 'army' | 'base';
    baseHp?: number;
    leaks?: number;
  }
): void {
  sessionState.levelsPlayed++;

  safeTrack('level_fail', {
    level_id: levelId,
    tier: tier,
    soldiers_at_death: soldiersAtDeath,
    fail_position: Math.round(failPosition * 100), // As percentage
    fail_reason: context?.reason,
    base_hp: context?.baseHp,
    leaks: context?.leaks,
  });
}

// =============================================================================
// GAMEPLAY EVENTS
// =============================================================================

/**
 * Tracks when the player passes through a multiplier gate
 */
export function trackGateChoice(
  levelId: number,
  gatePosition: number,
  multiplierChosen: number
): void {
  safeTrack('gate_choice', {
    level_id: levelId,
    gate_position: gatePosition,
    multiplier_chosen: multiplierChosen,
  });
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initializes analytics with session tracking
 * Sets up beforeunload handler for session_end
 */
export function initAnalytics(): void {
  // Track session start
  trackSessionStart();

  // Set up session end tracking on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      trackSessionEnd();
    });

    // Also track on visibility change (mobile browsers)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        trackSessionEnd();
      }
    });
  }
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Gets the current session duration in seconds
 */
export function getSessionDuration(): number {
  return Math.floor((Date.now() - sessionState.startTime) / 1000);
}

/**
 * Gets the number of levels played in this session
 */
export function getLevelsPlayed(): number {
  return sessionState.levelsPlayed;
}
